import { forwardRef } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { AlertBell } from "../components/AlertBell";
import { BugReportButton } from "../components/BugReportButton";
import { DeadlineClock } from "../components/DeadlineClock";
import { MotionToggle, ThemeToggle } from "../components/ThemeToggle";
import type { CurrentUser } from "../auth/useCurrentUser";
import { useMobile } from "../context/MobileContext";
import { AuditorBell } from "./AuditorBell";
import { UserMenu } from "./UserMenu";
import type { UserMenuProps } from "./UserMenu";

// ── Top bar ───────────────────────────────────────────────────────────────
// Hamburger (mobile) - breadcrumb / context slot - right cluster: role-specific
// bell, Report a problem (every role and edition), ThemeToggle, UserMenu. No bare
// "Sign out" button anywhere.

export type TopBarBell = "alerts" | "auditor" | null;

export interface AppTopBarProps {
  user: CurrentUser;
  isMobile: boolean;
  /** Whether the mobile drawer (`#app-nav`) is open, for the hamburger's aria-expanded. */
  mobileOpen?: boolean;
  onMenuClick: () => void;
  breadcrumb?: ReactNode;
  bell?: TopBarBell;
  /** Show the statutory deadline clock (client shell only — the counts it reads are the client shape). */
  deadlineClock?: boolean;
  userMenu: Omit<UserMenuProps, "user" | "compact">;
}

export const AppTopBar = forwardRef<HTMLButtonElement, AppTopBarProps>(function AppTopBar(
  { user, isMobile, mobileOpen = false, onMenuClick, breadcrumb, bell = null, deadlineClock = false, userMenu },
  menuButtonRef,
) {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        padding: isMobile ? "0 10px 0 6px" : "0 16px",
        height: 54,
        flexShrink: 0,
        borderBottom: "1px solid var(--c-border)",
        background: "var(--c-panel)",
        gap: 8,
        minWidth: 0,
      }}
    >
      {isMobile && (
        <button
          ref={menuButtonRef}
          type="button"
          onClick={onMenuClick}
          aria-label="Open navigation"
          aria-expanded={mobileOpen}
          aria-controls="app-nav"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "6px 8px",
            color: "var(--c-muted)",
            display: "flex",
            alignItems: "center",
            borderRadius: 3,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--c-panelAlt)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          <Menu size={18} />
        </button>
      )}

      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
        {breadcrumb}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {deadlineClock && <DeadlineClock />}
        {bell === "alerts" && <AlertBell />}
        {bell === "auditor" && <AuditorBell />}
        <BugReportButton appVersion={userMenu.version} />
        <MotionToggle />
        <ThemeToggle />
        <UserMenu user={user} compact={isMobile} {...userMenu} />
      </div>
    </header>
  );
});

/**
 * A "Clients / Name / Tab" breadcrumb for the shells that show one. On a phone
 * the slot beside the hamburger, the bells and the client switcher is about
 * 200px, so only the last crumb is shown (the earlier ones collapse to an
 * ellipsis link back to the first) and labels are capped tighter.
 */
export function Breadcrumb({ items }: { items: Array<{ label: string; to?: string; onClick?: () => void }> }) {
  const isMobile = useMobile();
  const shown = isMobile && items.length > 2
    ? [{ ...items[0], label: "\u2026" }, items[items.length - 1]]
    : items;
  return (
    <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, fontSize: 12.5, flex: "1 1 auto" }}>
      {shown.map((item, i) => {
        const last = i === shown.length - 1;
        // The collapsed crumb reads as the label it stands for (on the link, never on a bare span).
        const srLabel = item.label === "\u2026" ? items[0].label : undefined;
        const label = (
          <span style={{ color: last ? "var(--c-heading)" : "var(--c-muted)", fontWeight: last ? 600 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: isMobile ? 110 : 220 }}>
            {item.label}
          </span>
        );
        return (
          <span key={`${i}-${item.label}`} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            {i > 0 && <span aria-hidden="true" style={{ color: "var(--c-faint)" }}>/</span>}
            {!last && item.to ? (
              <Link to={item.to} onClick={item.onClick} aria-label={srLabel} style={{ textDecoration: "none", display: "flex", minWidth: 0 }}>
                {label}
              </Link>
            ) : !last && item.onClick ? (
              <button
                type="button"
                aria-label={srLabel}
                onClick={item.onClick}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", minWidth: 0, font: "inherit" }}
              >
                {label}
              </button>
            ) : (
              <span aria-current={last ? "page" : undefined} style={{ display: "flex", minWidth: 0 }}>{label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
